/**
 * The API client.
 *
 * Every backend response is `{ success, data, message }` (or `{ success:false, error }`),
 * so this unwraps the envelope once and everything above it deals in plain data.
 * A 401 triggers one refresh attempt, and concurrent 401s share that single refresh
 * rather than each firing their own.
 */

const BASE = '/api/v1';

const ACCESS_KEY = 'viganium.accessToken';
const REFRESH_KEY = 'viganium.refreshToken';

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, error: ApiErrorShape) {
    super(error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  // Collapse a burst of 401s into one refresh call.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      tokens.set(body.data.accessToken, body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Let the next 401 start a fresh attempt.
      setTimeout(() => (refreshInFlight = null), 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return request<T>(path, { ...options, _retried: true });
    tokens.clear();
    window.dispatchEvent(new CustomEvent('viganium:signed-out'));
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has expired.' });
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const error: ApiErrorShape = payload?.error ?? {
      code: 'NETWORK_ERROR',
      message: `Request failed (${res.status}).`,
    };
    if (res.status === 401) {
      tokens.clear();
      window.dispatchEvent(new CustomEvent('viganium:signed-out'));
    }
    throw new ApiError(res.status, error);
  }

  return payload?.data as T;
}

/** POST/PATCH responses may carry a toast-worthy message alongside the data. */
export async function requestWithMessage<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; message: string | null }> {
  const { method = 'GET', body, params } = options;
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return requestWithMessage<T>(path, { ...options, _retried: true });
  }

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: 'Request failed.' });
  }
  return { data: payload?.data as T, message: payload?.message ?? null };
}

interface UploadOptions {
  kind?: 'FILE' | 'VOICE_NOTE';
  durationMs?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** `POST /files/uploads` — the file is checked, and comes back with a ticket and where its bytes go. */
interface UploadStart {
  ticket: string;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

/**
 * Uploads one file, voice note or video, reporting progress as it goes, and
 * returns it to be attached by id when the task, ticket or reply is saved.
 *
 * Three steps: the API checks the file and says where its bytes go — straight
 * into Cloudflare R2 through a link it signed, or to the API itself when files
 * are kept on the server — the bytes go there, and the API keeps the file. So a
 * large video never passes through the server. An API from before this offers
 * only the one-step upload, which is used then.
 */
export async function uploadFile(blob: Blob, name: string, options: UploadOptions = {}): Promise<UploadedFile> {
  let start: UploadStart;
  try {
    start = await request<UploadStart>('/files/uploads', {
      method: 'POST',
      body: {
        name,
        type: blob.type,
        size: blob.size,
        kind: options.kind,
        durationMs: options.durationMs === undefined ? undefined : Math.round(options.durationMs),
      },
      signal: options.signal,
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return uploadWhole(blob, name, options);
    throw e;
  }
  await sendBytes(blob, start.upload, options);
  return request<UploadedFile>('/files/uploads/complete', {
    method: 'POST',
    body: { ticket: start.ticket },
    signal: options.signal,
  });
}

/**
 * A file into Viganium Drive (2026-11-02): the same three steps as `uploadFile`,
 * through Drive's own routes — which check that the folder takes it, and take any
 * kind of file — and what comes back is the item it became.
 */
export async function uploadToDrive<T>(
  blob: Blob,
  name: string,
  parentId: string | null,
  options: Omit<UploadOptions, 'kind' | 'durationMs'> = {},
): Promise<T> {
  const start = await request<UploadStart>('/drive/uploads', {
    method: 'POST',
    body: { parentId, name, type: blob.type, size: blob.size },
    signal: options.signal,
  });
  await sendBytes(blob, start.upload, options);
  return request<T>('/drive/uploads/complete', { method: 'POST', body: { ticket: start.ticket }, signal: options.signal });
}

/**
 * A file into a site (2026-11-03), at its path there: the same three steps, through
 * the site's own routes — which check that the caller looks after the site and that
 * a site takes the file — replacing any file at that path. What comes back is the
 * site's file it became.
 */
export async function uploadToSite<T>(
  blob: Blob,
  siteId: string,
  path: string,
  options: Omit<UploadOptions, 'kind' | 'durationMs'> = {},
): Promise<T> {
  const start = await request<UploadStart>(`/sites/${siteId}/uploads`, {
    method: 'POST',
    body: { path, size: blob.size },
    signal: options.signal,
  });
  await sendBytes(blob, start.upload, options);
  return request<T>(`/sites/${siteId}/uploads/complete`, {
    method: 'POST',
    body: { ticket: start.ticket },
    signal: options.signal,
  });
}

/** The bytes, to where the API said — XMLHttpRequest, since `fetch` cannot report an upload's progress. */
function sendBytes(blob: Blob, target: UploadStart['upload'], options: UploadOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
      return;
    }
    // R2 is another origin; the API's own route is this one.
    const toStorage = new URL(target.url, window.location.origin).origin !== window.location.origin;
    const xhr = new XMLHttpRequest();
    xhr.open(target.method, target.url);
    // Exactly these: an R2 link is signed with them.
    for (const [header, value] of Object.entries(target.headers)) xhr.setRequestHeader(header, value);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const settle = () => options.signal?.removeEventListener('abort', abort);

    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* R2 answers in XML, a proxy in HTML — neither is our envelope */
      }
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : toStorage
            ? { code: 'FILE_STORAGE_REFUSED', message: `The file storage refused the upload (${xhr.status}).` }
            : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    // A storage that does not let this page upload (its CORS policy) looks the same as no network.
    xhr.onerror = () => {
      settle();
      reject(
        new ApiError(
          0,
          toStorage
            ? { code: 'FILE_STORAGE_UNREACHABLE', message: 'The file storage could not be reached.' }
            : { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' },
        ),
      );
    };
    xhr.onabort = () => {
      settle();
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
    };
    xhr.send(blob);
  });
}

/**
 * The one-step upload, `POST /files/upload`: the whole file to the API in one
 * request. Only for an API that has no three-step upload yet.
 */
function uploadWhole(blob: Blob, name: string, options: UploadOptions = {}, retried = false): Promise<UploadedFile> {
  return new Promise<UploadedFile>((resolve, reject) => {
    const form = new FormData();
    // Fields before the file, so the server has them when the file arrives.
    if (options.kind) form.append('kind', options.kind);
    if (options.durationMs !== undefined) form.append('durationMs', String(Math.round(options.durationMs)));
    form.append('file', blob, name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/files/upload`);
    const access = tokens.access;
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });

    xhr.onload = async () => {
      options.signal?.removeEventListener('abort', abort);
      if (xhr.status === 401 && !retried && tokens.refresh && (await refreshTokens())) {
        uploadWhole(blob, name, options, true).then(resolve, reject);
        return;
      }
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* the proxy's own error page, not our envelope */
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) return resolve(payload.data as UploadedFile);
      // A 413 from the proxy in front of the API arrives as HTML, not as our error.
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    xhr.onerror = () => reject(new ApiError(0, { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' }));
    xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'));
    xhr.send(form);
  });
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { params }),
  post: <T>(path: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'POST', body, params }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};

// ---------------------------------------------------------------------------
// Shared response shapes
// ---------------------------------------------------------------------------

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface UserRef {
  id: string;
  fullName: string;
  fullNameAr?: string | null;
  email?: string;
  jobTitle?: string | null;
}

/** Where an employee works from (2026-10-21): the employee form sets it, Working now splits the team by it. */
export type WorkMode = 'OFFICE' | 'REMOTE';
export const WORK_MODES: readonly WorkMode[] = ['OFFICE', 'REMOTE'];

export interface DepartmentRef {
  id: string;
  name: string;
  nameAr?: string | null;
  code: string;
  /** Sent by `/departments/options` and the department list. */
  isActive?: boolean;
}

export interface ProjectRef {
  id: string;
  name: string;
  nameAr?: string | null;
  code: string;
  status?: string;
}

export type WorkflowRunStatus = 'ACTIVE' | 'COMPLETED' | 'STOPPED';

/**
 * A workflow run as a task carries it. Built on the task since 2026-10-15, it
 * has no name; one started before then from a saved workflow keeps that name.
 */
export interface WorkflowRunRef {
  id: string;
  status: WorkflowRunStatus;
  currentLevel: number;
  name: string | null;
  nameAr: string | null;
}

/** `returnTargets` on a step's page: someone who did the work earlier, and the level they did it on. */
export interface ReturnTarget extends UserRef {
  level: number;
}

/** Where the work handed on from a Leaders Task stands. */
export type HandedOnWork = 'none' | 'underWay' | 'finished' | 'calledOff';

/** `GET /leader-tasks/leaders` — a leader a Leaders Task can go to, with the departments they lead. */
export interface LeaderOption extends UserRef {
  departments: Array<{ id: string; name: string; nameAr: string | null }>;
}

/** `GET /leader-tasks/:id/hand-on` — whom the leader who has it can hand it to. */
export interface HandOnOptions {
  leader: UserRef;
  departments: Array<{
    id: string;
    name: string;
    nameAr: string | null;
    /** `onProject` is null when no project is named. */
    people: Array<UserRef & { onProject: boolean | null }>;
  }>;
}

/** `task.leaderWork` on a Leaders Task's page — the work handed on from it. */
export interface LeaderWork {
  state: HandedOnWork;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    startAt: string | null;
    dueAt: string | null;
    completedAt: string | null;
    createdAt: string;
    workflowRunId: string | null;
    workflowLevel: number | null;
    assignee: UserRef | null;
    /** A workflow's step out for a check (2026-10-15): its own person, who gets it back. */
    returnTo?: UserRef | null;
    /** Given to a whole department, which shares it (2026-10-10). */
    assignedToDepartment?: boolean;
    department?: { id: string; name: string; nameAr: string | null } | null;
    /** Whether this person may open it — a workflow's steps are private. */
    canOpen: boolean;
    workflowRun: (WorkflowRunRef & { levelCount: number }) | null;
  }>;
}

/** A stored file as the API hands it out: never its storage path, always signed links. */
export interface UploadedFile {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  kind: 'FILE' | 'VOICE_NOTE';
  /** A recording's length, as the recorder measured it. */
  durationMs: number | null;
  createdAt: string;
  uploadedBy: UserRef | null;
  /** Shows it in the page — a picture, a PDF, a voice note. Works without a session. */
  url: string;
  /** Saves it. */
  downloadUrl: string;
}

/** A file on a task, ticket or Q Task. `commentId` is set when it came with a reply. */
export interface Attachment {
  id: string;
  commentId: string | null;
  createdAt: string;
  file: UploadedFile;
}

/** Who an announcement is for: everyone, people picked by name, or whoever is in the departments picked. */
export type AnnouncementAudience = 'ALL' | 'USERS' | 'DEPARTMENTS';

/** A row of `GET /announcements`, and of the dashboard's card. */
export interface AnnouncementRow {
  id: string;
  title: string;
  /** The start of the details, on one line. */
  excerpt: string;
  audience: AnnouncementAudience;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy: UserRef;
  departments: DepartmentRef[];
  /** With USERS: how many people it names. */
  peopleCount: number;
  files: number;
  voiceNotes: number;
  addressedToMe: boolean;
  /** When the caller first opened it, if it is for them. */
  seenAt: string | null;
  unread: boolean;
  /** For whoever keeps it — its author, the admin: how many opened it, and tasks made from it. */
  seenCount: number | null;
  taskCount: number | null;
  can: { edit: boolean; delete: boolean };
}

/** `GET /announcements/:id`. */
export interface AnnouncementDetail {
  id: string;
  title: string;
  body: string | null;
  audience: AnnouncementAudience;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy: UserRef;
  users: UserRef[];
  departments: DepartmentRef[];
  attachments: Attachment[];
  /** Tasks made from it that the caller may open: the open urgent ones first, then the oldest first. */
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    dueAt: string | null;
    createdAt: string;
    assignee: UserRef | null;
    /** Given to a whole department, which shares it (2026-10-10). */
    assignedToDepartment?: boolean;
    department?: { id: string; name: string; nameAr: string | null } | null;
    createdBy: UserRef;
  }>;
  addressedToMe: boolean;
  seenAt: string | null;
  can: { edit: boolean; delete: boolean; attach: boolean; history: boolean; createTask: boolean };
  /** For whoever keeps it: who has seen it and when, and who it is for that has not yet. */
  reach: { people: number; seen: number; seenBy: Array<{ seenAt: string; user: UserRef }>; notSeen: UserRef[] } | null;
  /** For whoever keeps it: everything done with it, newest first. */
  history: Array<{
    id: string;
    action: string;
    message: string | null;
    metadata: any;
    occurredAt: string;
    actor: UserRef | null;
  }> | null;
  /** After posting or changing it: how many people were just told. */
  notified?: number;
}

/** `GET /announcements/audience` — what the form offers. */
/**
 * `GET /announcements/audience` — what this person may send to. Administrators:
 * everyone, anybody, every department. Anyone else, team leaders included since
 * 2026-10-07: the departments they are in and the people in them, and not everyone.
 */
export interface AnnouncementAudienceOptions {
  /** May send to anyone (`announcements.any_audience`). */
  anyone: boolean;
  /** Everyone but you — null when you may not send to everyone. */
  everyone: number | null;
  departments: Array<DepartmentRef & { people: number }>;
  /** The people you may pick, never yourself. */
  people: UserRef[];
}

/** A folder of internal mail. */
export type MailFolder = 'inbox' | 'sent' | 'trash';

/** Someone on a mail. Not active: no longer an employee, so nothing more can be sent to them. */
export interface MailPerson extends UserRef {
  isActive: boolean;
}

/** A row of `GET /mailbox` — one mail in a folder of the caller's. */
export interface MailRow {
  id: string;
  subject: string;
  /** The start of the content, on one line. */
  excerpt: string;
  createdAt: string;
  sender: MailPerson;
  /** The first few people it went to, by name; `toCount` is all of them. */
  to: MailPerson[];
  toCount: number;
  /** Sent by the caller. */
  mine: boolean;
  folder: MailFolder | null;
  /** Sent to the caller, who has not read it. */
  unread: boolean;
  /** For whoever sent it: how many of the people it went to have opened it. */
  readCount: number | null;
  files: number;
  voiceNotes: number;
  isReply: boolean;
  isForward: boolean;
}

/** `GET /mailbox/:id` — one mail, for someone on it. */
export interface MailDetail {
  id: string;
  subject: string;
  /** The content as the editor wrote it, cleaned by the API. Null: the subject and files say it all. */
  bodyHtml: string | null;
  createdAt: string;
  sender: MailPerson;
  /** Everyone it went to. `readAt` — when each first opened it — only for whoever sent it. */
  recipients: Array<MailPerson & { readAt?: string | null }>;
  attachments: Attachment[];
  /** The mail it answers, or forwards — when the caller has a copy of that one too. */
  replyTo: { id: string; subject: string } | null;
  forwardOf: { id: string; subject: string } | null;
  mine: boolean;
  folder: MailFolder | null;
  /** The caller's own copy: read, as their Inbox shows it. */
  readAt: string | null;
  /** Who Reply and Reply all go to: active people only, never the caller. */
  targets: { reply: string[]; replyAll: string[] };
  can: {
    reply: boolean;
    replyAll: boolean;
    forward: boolean;
    markRead: boolean;
    markUnread: boolean;
    trash: boolean;
    restore: boolean;
    purge: boolean;
  };
  /** After sending it: how many people it went to. */
  notified?: number;
}

/** `GET /mailbox/recipients` — every active employee but you, whatever their department. */
export interface MailRecipientOption extends UserRef {
  departments: Array<{ id: string; name: string; nameAr: string | null }>;
}

/** `GET /files/policy` — what the upload control checks before sending anything. */
export interface FilePolicy {
  /** Pictures, documents and recordings. */
  maxUploadMb: number;
  /** Videos. An API from before videos sends none. */
  maxVideoMb?: number;
  /** Any file put in Viganium Drive. An API from before Drive sends none. */
  maxDriveFileMb?: number;
  extensions: string[];
  /** The extensions that are always a video; a webm or an ogg goes by the browser's type. */
  videoExtensions?: string[];
  maxFilesPerPost: number;
  maxVoiceNoteSeconds: number;
}

export type AppSystem = 'ADMIN' | 'SYS' | 'CLIENTS';

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode: string | null;
  jobTitle: string | null;
  phone: string | null;
  locale: string;
  isActive: boolean;
  lastLoginAt: string | null;
  /** Where they work from: those at the office may ask to work remotely. An API from before it sends none. */
  workMode?: WorkMode;
  /** The system this session signed in to — always ADMIN here. */
  app: AppSystem;
  /** Every system the person can open; the sidebar links to sys when it lists it. */
  systems: AppSystem[];
  /** Admin roles only: sys roles are given, and seen, in sys. */
  roles: Array<{ code: string; name: string; nameAr: string | null }>;
  permissions: string[];
  /**
   * Their Finance roles handle payroll. Only then do the employee pages show,
   * ask for and change a basic salary — the API sends nobody else a figure.
   */
  salaryAccess?: boolean;
  /** Settings › Sites picked them to publish sites — by name, or by a department of theirs. An API from before says nothing. */
  sitePublisher?: boolean;
  /** They may open the Executive Dashboard (an Administrator with Dashboard Access): where it is. Missing or null otherwise. */
  executiveDashboard?: { url: string } | null;
  departments: Array<DepartmentRef & { memberType: 'MEMBER' | 'LEADER' }>;
}

/** One salary record's figure, as the employee pages show it. */
export interface SalaryFigure {
  basicSalary: string;
  currency: string;
  /** `YYYY-MM-DD`. */
  effectiveFrom: string;
}

/** `salary` on `GET /users/:id` — there only for someone with `salaryAccess`. */
export interface EmployeeSalary {
  /** The record payroll pays from today. */
  current: SalaryFigure | null;
  /** A raise already booked to start later — or a first salary that has not started. */
  scheduled: SalaryFigure | null;
  /**
   * `YYYY-MM-DD`: the first day a change may start on; null for any day. A
   * booked salary may move earlier than itself. Missing from an older API.
   */
  earliestFrom?: string | null;
}
